import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  Pagination,
  Tooltip,
  Divider,
} from "@mui/material";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import config from "../../config";
import { useNavigate } from "react-router-dom";

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function ValidationLogs() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [isValidFilter, setIsValidFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState([]);
  const [counts, setCounts] = useState({ total: 0, valid: 0, invalid: 0 });
  const [totalPages, setTotalPages] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();


 const fetchLogs = useCallback(async () => {
  try {
    setRefreshing(true);
    setError("");

    const token = sessionStorage.getItem("token");

    const res = await fetch(
      `${config.apiBaseUrl}/market-survey/file-validation-logs?date=${selectedDate}&isValid=${isValidFilter}&page=${page}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await res.json();

    if (data.success) {
      setLogs(data.data || []);
      setCounts(data.counts || { total: 0, valid: 0, invalid: 0 });
      setTotalPages(data.totalPages || 1);
    } else {
      setLogs([]);
      setCounts({ total: 0, valid: 0, invalid: 0 });
      setTotalPages(1);
      setError(data.message || "Failed to load report");
    }
  } catch (err) {
    setLogs([]);
    setCounts({ total: 0, valid: 0, invalid: 0 });
    setTotalPages(1);
    setError("Something went wrong while loading the report");
  } finally {
    setRefreshing(false);
  }
}, [selectedDate, isValidFilter, page]);

useEffect(() => {
  fetchLogs();
}, [fetchLogs]);

  const handleDateChange = (e) => {
    setPage(1);
    setSelectedDate(e.target.value);
  };

  const handleFilterChange = (e) => {
    setPage(1);
    setIsValidFilter(e.target.value);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  const formatCallAt = (value) => {
    if (!value) return "—";

    try {
      const date = new Date(value);

      if (isNaN(date.getTime())) {
        return "—";
      }

      return `${date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })} ${date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })}`;
    } catch {
      return "—";
    }
  };
  const isTodaySelected = selectedDate === getTodayDate();
  const rangeLabel =
    logs.length > 0 ? `${logs.length} record${logs.length > 1 ? "s" : ""}` : "";

  const NoData = () => (
    <Paper
      sx={{
        p: 6,
        textAlign: "center",
        borderRadius: 3,
        border: "1px dashed #D1D5DB",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "16px",
          background: "#F1F5F9",
          color: "#94A3B8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 2,
        }}
      >
        <InboxRoundedIcon fontSize="medium" />
      </Box>
      <Typography variant="h6" fontWeight={700} sx={{ color: "#111827" }}>
        No records found
      </Typography>
      <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
        {isTodaySelected
          ? "No files have been validated yet today."
          : "Try picking a different date or status."}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            rowGap={2}
            mb={3}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isMobile && (
                <IconButton
                  onClick={() => setMobileOpen(true)}
                  sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", background: "#fff" }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              )}

              <IconButton
                onClick={() => navigate("/report")}
                sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", background: "#fff" }}
              >
                <ArrowBackRoundedIcon />
              </IconButton>

              <Box>
                <Typography variant={isMobile ? "h6" : "h4"} fontWeight={800} sx={{ color: "#111827" }}>
                  Validation Logs
                </Typography>
                {!isMobile && (
                  <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.3 }}>
                    Automated file validation results by date
                  </Typography>
                )}
              </Box>

              {refreshing && <CircularProgress size={18} sx={{ ml: 0.5 }} />}
            </Stack>
          </Stack>

          <Paper
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: 2.5,
              borderRadius: 3,
              boxShadow: "0 2px 10px rgba(15,23,42,0.05)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <FilterListRoundedIcon fontSize="small" sx={{ color: "#6B7280" }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#374151" }}>
                Filters
              </Typography>
              {isTodaySelected && (
                <Chip
                  label="Today"
                  size="small"
                  sx={{ height: 20, fontSize: "0.7rem", background: "#EFF6FF", color: "#2563EB", fontWeight: 600 }}
                />
              )}
            </Stack>

            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={5} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                  label="Date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={isValidFilter}
                  onChange={handleFilterChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Valid</MenuItem>
                  <MenuItem value="false">Invalid</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={3} md={2}>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={fetchLogs}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "10px",
                      width: 40,
                      height: 40,
                    }}
                  >
                    <RefreshRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={1.5} mb={2.5}>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  boxShadow: "0 2px 10px rgba(15,23,42,0.05)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "#EFF6FF",
                    color: "#2563EB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <DescriptionRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    Total Files
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: "#111827" }}>
                    {counts.total}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={4}>
              <Paper
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  boxShadow: "0 2px 10px rgba(15,23,42,0.05)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "#ECFDF5",
                    color: "#059669",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CheckCircleRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    Valid
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: "#111827" }}>
                    {counts.valid}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={4}>
              <Paper
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  boxShadow: "0 2px 10px rgba(15,23,42,0.05)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "#FEF2F2",
                    color: "#DC2626",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CancelRoundedIcon />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    Invalid
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ color: "#111827" }}>
                    {counts.invalid}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {error && (
            <Paper
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 2,
                background: "#FEF2F2",
                color: "#DC2626",
                boxShadow: "none",
                border: "1px solid #FECACA",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {error}
              </Typography>
            </Paper>
          )}

          {!refreshing && !error && (
            <Typography variant="body2" sx={{ color: "#6B7280", mb: 1.5 }}>
              {rangeLabel
                ? `${rangeLabel} for ${new Date(selectedDate).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}`
                : "No records to show"}
            </Typography>
          )}

          <Box sx={{ position: "relative" }}>
            {refreshing && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(248,250,252,0.6)",
                  borderRadius: 3,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <Box
              sx={{
                opacity: refreshing ? 0.5 : 1,
                pointerEvents: refreshing ? "none" : "auto",
                transition: "opacity 0.15s ease",
              }}
            >
              {isMobile ? (
                <Stack spacing={1.5}>
                  {logs.length > 0 ? (
                    logs.map((log) => (
                      <Paper
                        key={log._id}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          boxShadow: "0 2px 10px rgba(15,23,42,0.05)",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                            <InsertDriveFileRoundedIcon
                              fontSize="small"
                              sx={{ color: "#9CA3AF", mt: "2px", flexShrink: 0 }}
                            />
                            <Typography
                              fontWeight={700}
                              variant="body2"
                              sx={{ wordBreak: "break-word", color: "#111827" }}
                            >
                              {log.fileName}
                            </Typography>
                          </Stack>

                          <Chip
                            label={log.isValid ? "Valid" : "Invalid"}
                            size="small"
                            sx={{
                              flexShrink: 0,
                              fontWeight: 600,
                              background: log.isValid ? "#ECFDF5" : "#FEF2F2",
                              color: log.isValid ? "#059669" : "#DC2626",
                            }}
                          />
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        {log.mandiPartner ? (
                          <Stack spacing={0.75}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <StorefrontRoundedIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                              <Typography variant="body2" fontWeight={600} sx={{ color: "#111827" }}>
                                {log.mandiPartner.mandiPartner}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PlaceRoundedIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                                {log.mandiPartner.mandiLocation} · {log.mandiPartner.businessName}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PhoneRoundedIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                                {log.mandiPartner.contactNumber}
                              </Typography>
                            </Stack>
                          </Stack>
                        ) : (
                          log.reasons?.length > 0 && (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              {log.reasons.map((reason, i) => (
                                <Chip
                                  key={i}
                                  label={reason}
                                  size="small"
                                  sx={{
                                    background: "#FEF2F2",
                                    color: "#DC2626",
                                    fontWeight: 500,
                                    mb: 0.5,
                                  }}
                                />
                              ))}
                            </Stack>
                          )
                        )}


                        <Typography
                          variant="caption"
                          sx={{
                            color: "#6B7280",
                            display: "block",
                            mt: 1.5,
                            fontWeight: 600,
                          }}
                        >
                          Call At: {formatCallAt(log.callingDate)}
                        </Typography>
                        {/* <Typography
                          variant="caption"
                          sx={{ color: "#9CA3AF", display: "block", mt: 1.5 }}
                        >
                          {formatDateTime(log.validationDate)}
                        </Typography> */}

                      </Paper>
                    ))
                  ) : (
                    <NoData />
                  )}
                </Stack>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 2px 10px rgba(15,23,42,0.05)",
                    overflow: "hidden",
                  }}
                >
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {["File Name", "Status", "Mandi Partner", "Details / Reason", "Calling Date", "Validate At"].map(
                            (label) => (
                              <TableCell
                                key={label}
                                sx={{
                                  background: "#F8FAFC",
                                  color: "#374151",
                                  fontWeight: 700,
                                  fontSize: "0.78rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  borderBottom: "1px solid #E5E7EB",
                                }}
                              >
                                {label}
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {logs.length > 0 ? (
                          logs.map((log, idx) => (
                            <TableRow
                              key={log._id}
                              hover
                              sx={{
                                background: idx % 2 === 1 ? "#FAFBFC" : "#fff",
                                "&:last-child td": { borderBottom: 0 },
                              }}
                            >
                              <TableCell sx={{ maxWidth: 320 }}>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                  <InsertDriveFileRoundedIcon
                                    fontSize="small"
                                    sx={{ color: "#9CA3AF", mt: "2px", flexShrink: 0 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ wordBreak: "break-word", color: "#374151" }}
                                  >
                                    {log.fileName}
                                  </Typography>
                                </Stack>
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={log.isValid ? "Valid" : "Invalid"}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    background: log.isValid ? "#ECFDF5" : "#FEF2F2",
                                    color: log.isValid ? "#059669" : "#DC2626",
                                  }}
                                />
                              </TableCell>

                              <TableCell>
                                {log.mandiPartner ? (
                                  <Box>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: "#111827" }}>
                                      {log.mandiPartner.mandiPartner}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                      {log.mandiPartner.mandiLocation}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                                    —
                                  </Typography>
                                )}
                              </TableCell>

                              <TableCell sx={{ maxWidth: 280 }}>
                                {log.mandiPartner ? (
                                  <>
                                    <Typography variant="body2" sx={{ color: "#374151" }}>
                                      {log.mandiPartner.businessName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                      {log.mandiPartner.contactNumber}
                                    </Typography>
                                  </>
                                ) : (
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {(log.reasons || []).map((reason, i) => (
                                      <Chip
                                        key={i}
                                        label={reason}
                                        size="small"
                                        sx={{
                                          background: "#FEF2F2",
                                          color: "#DC2626",
                                          fontWeight: 500,
                                          mb: 0.5,
                                        }}
                                      />
                                    ))}
                                    {(!log.reasons || log.reasons.length === 0) && (
                                      <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                                        —
                                      </Typography>
                                    )}
                                  </Stack>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#374151",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {formatCallAt(log.callingDate)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: "#374151" }}>
                                  {formatDateTime(log.validationDate)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ border: 0 }}>
                              <NoData />
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* PAGINATION */}
              {logs.length > 0 && totalPages > 1 && (
                <Stack alignItems="center" mt={4}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                  />
                </Stack>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}